
import React from 'react';
import { UserSettings, Language } from '../types';
import { ChevronLeft, Palette, Type, Eye, MousePointer2 } from 'lucide-react';
import { THEMES } from '../App';

interface Props {
  settings: UserSettings;
  onChange: (settings: UserSettings) => void;
  onClose: () => void;
}

const FONTS = ['JetBrains Mono', 'Inter', 'Roboto Mono'];

export const SettingsView: React.FC<Props> = ({ settings, onChange, onClose }) => {
  return (
    <div className="w-full max-w-5xl animate-fadeIn custom-scrollbar overflow-y-auto pb-20">
      <div className="flex items-center space-x-4 mb-12">
        <button onClick={onClose} className="text-sub hover:text-main transition-colors">
          <ChevronLeft size={32} />
        </button>
        <h2 className="text-3xl font-bold">Settings</h2>
      </div>

      <div className="space-y-12">
        {/* Theme Selection */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2 text-sub uppercase text-xs font-bold tracking-widest">
              <Palette size={14} />
              <span>Theme</span>
            </div>
            <span className="text-[10px] text-sub uppercase tracking-tighter italic">20 Curated Styles</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Object.entries(THEMES).map(([id, t]) => (
              <button
                key={id}
                onClick={() => onChange({ ...settings, theme: id })}
                className={`p-3 rounded-xl border-2 transition-all group relative overflow-hidden ${settings.theme === id ? 'border-main' : 'border-transparent bg-sub/5 hover:bg-sub/10'}`}
                style={{ backgroundColor: t.bg }}
              >
                <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-bold truncate uppercase tracking-tight" style={{ color: t.text }}>{id.replace('_', ' ')}</span>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.main }}></div>
                </div>
                <div className="flex space-x-1">
                   <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: t.main, opacity: 0.6 }}></div>
                   <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: t.sub, opacity: 0.6 }}></div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 bg-sub/5 p-8 rounded-2xl">
          <div>
            <div className="flex items-center space-x-2 mb-6 text-sub uppercase text-xs font-bold tracking-widest">
              <Type size={14} />
              <span>Font Family</span>
            </div>
            <div className="flex flex-col space-y-2">
              {FONTS.map(f => (
                <button
                  key={f}
                  onClick={() => onChange({ ...settings, fontFamily: f })}
                  className={`px-4 py-3 rounded-xl text-left text-sm transition-all border-2 ${settings.fontFamily === f ? 'border-main text-main font-bold' : 'border-transparent bg-sub/5 text-sub hover:text-text'}`}
                  style={{ fontFamily: f }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-between mb-6 text-sub uppercase text-xs font-bold tracking-widest">
              <div className="flex items-center space-x-2">
                <Type size={14} />
                <span>Font Size</span>
              </div>
              <span className="text-main font-bold">{settings.fontSize}px</span>
            </div>
            <div className="relative pt-6 pb-2">
              <input 
                type="range" min="14" max="42" step="1" 
                value={settings.fontSize} 
                onChange={(e) => onChange({ ...settings, fontSize: parseInt(e.target.value) })}
                className="w-full accent-main h-1.5 bg-sub/20 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-2 text-[10px] text-sub uppercase">
                <span>Tiny</span>
                <span>Normal</span>
                <span>Giant</span>
              </div>
            </div>
            
            <div className="mt-8 p-4 rounded-xl border border-sub/20 text-center opacity-60">
                <p style={{ fontSize: settings.fontSize, fontFamily: settings.fontFamily }}>Preview Text Size</p>
            </div>
          </div>
        </section>

        {/* Behavior */}
        <section className="bg-sub/5 p-8 rounded-2xl">
          <div className="flex items-center space-x-2 mb-6 text-sub uppercase text-xs font-bold tracking-widest">
            <Eye size={14} />
            <span>Behavior</span>
          </div>
          <div className="space-y-4">
            <label className="flex items-center justify-between p-5 bg-sub/5 hover:bg-sub/10 rounded-2xl cursor-pointer group transition-all">
                <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg transition-colors ${settings.focusMode ? 'bg-main text-black' : 'bg-sub/20 text-sub'}`}>
                        <Eye size={18} />
                    </div>
                    <div>
                        <span className="text-sm font-bold block">Concentration Mode</span>
                        <span className="text-xs text-sub">UI elements fade out while you type</span>
                    </div>
                </div>
                <input 
                type="checkbox" 
                checked={settings.focusMode} 
                onChange={(e) => onChange({ ...settings, focusMode: e.target.checked })}
                className="w-5 h-5 accent-main"
                />
            </label>

            <div className="flex items-center space-x-3 p-4 bg-main/5 rounded-xl border border-main/10 text-xs text-sub italic">
                <MousePointer2 size={14} className="text-main" />
                <span>Tip: You can also toggle this by clicking any empty space on the screen!</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
